import { NoticeSessionAppeal } from '@models/planning/notice-session-appeal.model'
import {
	SupplierEnrollmentNoticeAttachmentType,
	SupplierEnrollmentNoticeCounterargumentStatus,
	SupplierEnrollmentNoticeCounterargumentType
} from '@models/supplier/notice/supplier-enrollment-notice.model'
import {
	SupplierEnrollmentNoticeQualificationStatus,
	SupplierEnrollmentNoticeQualificationTargetStatus
} from '@models/supplier/notice/qualification/supplier-enrollment-notice-qualification.model'
import { SupplierEnrollmentNoticeTradingStatus } from '@models/supplier/notice/proposal/supplier-enrollment-notice-trading.model'

export type EnrollmentPublicRecordSection =
	| 'documents'
	| 'adjusted'
	| 'feasibility'
	| 'representation'
	| 'qualification'
	| 'appeal'
	| 'counterargument'
	| 'trading'

export type EnrollmentPublicRecordRequestStatus = 'OPENED' | 'CLOSED' | 'ANSWERED' | 'CANCELED'

export interface EnrollmentPublicRecordDocument {
	attachmentId?: string
	type?: SupplierEnrollmentNoticeAttachmentType
	created?: string
}

export interface EnrollmentPublicRecordAnswer {
	description?: string
	created?: string
	attachmentId?: string
	complementary?: boolean
}

export interface EnrollmentPublicRecordRequest {
	created?: string
	expiresAt?: string
	status?: EnrollmentPublicRecordRequestStatus
	description?: string
	answers?: EnrollmentPublicRecordAnswer[]
}

export interface EnrollmentPublicRecordQualificationTarget {
	description?: string
	status?: SupplierEnrollmentNoticeQualificationTargetStatus
	judgedAt?: string
	message?: string
	reopened?: number
}

export interface EnrollmentPublicRecordQualification {
	created?: string
	expiresAt?: string
	status?: SupplierEnrollmentNoticeQualificationStatus
	description?: string
	targets?: EnrollmentPublicRecordQualificationTarget[]
	answers?: EnrollmentPublicRecordAnswer[]
	documents?: EnrollmentPublicRecordDocument[]
}

export interface EnrollmentPublicRecordJudging {
	description?: string
	created?: string
	attachmentId?: string
}

export interface EnrollmentPublicRecordCounterargument {
	created?: string
	type?: SupplierEnrollmentNoticeCounterargumentType
	status?: SupplierEnrollmentNoticeCounterargumentStatus
	supplierName?: string
	supplierDocument?: string
	description?: string
	attachmentId?: string
	judging?: EnrollmentPublicRecordJudging
	counterarguments?: EnrollmentPublicRecordCounterargument[]
}

export interface EnrollmentPublicRecordTrading {
	created?: string
	updated?: string
	status?: SupplierEnrollmentNoticeTradingStatus
	description?: string
	items?: number
}

export interface EnrollmentPublicRecordSectionInfo {
	key: EnrollmentPublicRecordSection
	label: string
	icon: string
	total: number
}

/**
 * Os atos públicos de uma inscrição na disputa (spec 067), em leitura, para os outros participantes e para o visitante. O
 * servidor só devolve depois da abertura da sessão, e sem os textos livres enquanto a identidade estiver oculta. O
 * `attachmentId` é o que a rota de download da mesma leitura aceita; para o visitante, um rótulo opaco.
 */
export class EnrollmentPublicRecord {
	textsWithheld = false
	documents: EnrollmentPublicRecordDocument[] = []
	adjustedProposals: EnrollmentPublicRecordRequest[] = []
	feasibilities: EnrollmentPublicRecordRequest[] = []
	representations: EnrollmentPublicRecordRequest[] = []
	qualifications: EnrollmentPublicRecordQualification[] = []
	appeals: NoticeSessionAppeal[] = []
	counterarguments: EnrollmentPublicRecordCounterargument[] = []
	trading: EnrollmentPublicRecordTrading | null = null
	/** Os atos que existem, na ordem do menu do participante. */
	sections: EnrollmentPublicRecordSectionInfo[] = []

	constructor(source?: Partial<EnrollmentPublicRecord>) {
		if (source) {
			this.textsWithheld = !!source.textsWithheld
			this.documents = source.documents ?? []
			this.adjustedProposals = source.adjustedProposals ?? []
			this.feasibilities = source.feasibilities ?? []
			this.representations = source.representations ?? []
			this.qualifications = source.qualifications ?? []
			this.appeals = (source.appeals ?? []).map(it => new NoticeSessionAppeal(it))
			this.counterarguments = source.counterarguments ?? []
			this.trading = source.trading ?? null
		}

		this.sections = this.buildSections()
	}

	private buildSections(): EnrollmentPublicRecordSectionInfo[] {
		const sections: EnrollmentPublicRecordSectionInfo[] = [
			{ key: 'documents', label: 'Outros documentos', icon: 'paperclip', total: this.documents.length },
			{ key: 'adjusted', label: 'Proposta readequada', icon: 'file-pen', total: this.adjustedProposals.length },
			{ key: 'feasibility', label: 'Exequibilidade', icon: 'check-circle', total: this.feasibilities.length },
			{ key: 'representation', label: 'Diligência', icon: 'clipboard-check', total: this.representations.length },
			{ key: 'qualification', label: 'Habilitação', icon: 'file-contract', total: this.qualifications.length },
			{ key: 'appeal', label: 'Recursos', icon: 'scale-balanced', total: this.appeals.length },
			{ key: 'counterargument', label: 'Recursos/Contrarrazões', icon: 'file-shield', total: this.counterarguments.length },
			{ key: 'trading', label: 'Negociação', icon: 'handshake', total: this.trading ? 1 : 0 }
		]

		return sections.filter(it => it.total > 0)
	}

	sectionLabel(section: EnrollmentPublicRecordSection): string {
		return this.sections.find(it => it.key === section)?.label ?? ''
	}
}
